/**
 * Duration parsing + formatting for latency analysis
 * (docs/superpowers/plans/2026-07-07-latency-analysis.md).
 *
 * A latency field value is one authored identifier token — `500ms`,
 * `50ms-500ms`, `5s`, `1min`, `2day`, `1d-3d`. It is interpreted (never parsed
 * by the language) as a duration **interval** in seconds. Only the calc time
 * family counts; a token resolving to any other family (`2w` → custom "writes",
 * `500` → no unit) is malformed. Reuses {@link resolveUnit} so the accepted
 * unit set — including calc's spelled-out and plural aliases — stays identical
 * to the rest of calc.
 *
 * Formatting is display-only, one-significant-figure (the `~` back-of-envelope
 * spirit), picking the largest time unit that keeps the mantissa readable.
 */

import { resolveUnit, TIME } from "./units.js";

/** An inclusive duration interval, both ends in seconds. A point value has lo === hi. */
export interface DurationInterval {
  readonly lo: number;
  readonly hi: number;
}

export type DurationParse =
  | ({ readonly ok: true } & DurationInterval)
  | { readonly ok: false; readonly reason: "empty" | "not-time" | "malformed" };

const ONE = /^(\d+(?:\.\d+)?)\s*([A-Za-z]+)$/;

/** Parse one `<number><unit>` end into seconds; null when not a time quantity. */
function parseOne(tok: string): number | null {
  const m = ONE.exec(tok.trim());
  if (!m) return null;
  const value = Number.parseFloat(m[1]!);
  if (!Number.isFinite(value) || value < 0) return null;
  const unit = resolveUnit(m[2]!);
  if (unit.family !== "time") return null;
  return value * unit.factor;
}

/**
 * Parse a latency field value into a seconds interval.
 *
 * - `""` → `empty`
 * - a single end (`500ms`) → point interval (lo === hi)
 * - two ends (`50ms-500ms`) → `[min, max]` (a reversed range is normalized)
 * - any end that is not a time quantity → `not-time`
 * - anything else (extra dashes, unparseable) → `malformed`
 */
export function parseDuration(raw: string): DurationParse {
  const s = raw.trim();
  if (s === "") return { ok: false, reason: "empty" };
  const parts = s.split("-");
  if (parts.length === 1) {
    const v = parseOne(parts[0]!);
    if (v === null) return { ok: false, reason: reasonFor(parts[0]!) };
    return { ok: true, lo: v, hi: v };
  }
  if (parts.length === 2) {
    const a = parseOne(parts[0]!);
    const b = parseOne(parts[1]!);
    if (a === null || b === null) {
      // "not-time" only when both ends are shaped like quantities but off-family;
      // a structurally broken end is "malformed".
      const bad = a === null ? parts[0]! : parts[1]!;
      return { ok: false, reason: reasonFor(bad) };
    }
    return { ok: true, lo: Math.min(a, b), hi: Math.max(a, b) };
  }
  return { ok: false, reason: "malformed" };
}

/** A number-with-alpha-suffix that merely resolved off-family is `not-time`;
 *  anything not even shaped like `<number><unit>` is `malformed`. */
function reasonFor(tok: string): "not-time" | "malformed" {
  return ONE.test(tok.trim()) ? "not-time" : "malformed";
}

const TIME_TABLE: ReadonlyArray<readonly [string, number]> = [
  ["ms", TIME.ms!],
  ["s", TIME.s!],
  ["min", TIME.min!],
  ["h", TIME.h!],
  ["d", TIME.d!],
  ["wk", TIME.wk!],
  ["mo", TIME.mo!],
  ["y", TIME.y!],
];

/** One significant figure, with a half-step for a leading 1–3 (mirrors calc `~{}`). */
function sigFig1(v: number): number {
  if (!Number.isFinite(v) || v === 0) return 0;
  const k = Math.floor(Math.log10(v));
  const m = v / 10 ** k; // mantissa in [1, 10)
  const lead = Math.floor(m);
  let rm = lead <= 3 ? Math.round(m * 2) / 2 : Math.round(m);
  let exp = k;
  if (rm >= 10) {
    rm /= 10;
    exp += 1;
  }
  return Number.parseFloat((rm * 10 ** exp).toPrecision(12));
}

/** Format a seconds value as a one-sig-fig duration (`0.5 → "500ms"`, `120 → "2min"`). */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return "∞";
  if (seconds === 0) return "0";
  let idx = 0;
  for (let i = 0; i < TIME_TABLE.length; i++) {
    if (seconds >= TIME_TABLE[i]![1]) idx = i;
  }
  let [label, factor] = TIME_TABLE[idx]!;
  let mantissa = sigFig1(seconds / factor);
  if (mantissa >= 1000 && idx < TIME_TABLE.length - 1) {
    idx += 1;
    [label, factor] = TIME_TABLE[idx]!;
    mantissa = sigFig1(seconds / factor);
  }
  const num = mantissa.toLocaleString("en-US", { maximumFractionDigits: 20, useGrouping: false });
  return `${num}${label}`;
}

/** Format a seconds interval — a point renders as one value, else `lo–hi`. */
export function formatInterval(lo: number, hi: number): string {
  if (lo === hi) return formatDuration(lo);
  return `${formatDuration(lo)}–${formatDuration(hi)}`;
}
